import {
  createContext,
  ReactNode,
  useRef,
  useMemo,
  useContext,
  useCallback,
  useState,
} from 'react';
import debounce from 'lodash/debounce';

import { useGetInteractiveLessonParams } from 'approot/activity-builder/activity-builder.hooks';
import { useSnackbar } from 'approot/shared/snackbar-notification/snackbar-notification.hooks';
import type { UpdateActivitiesComponentInput } from '../../../../gql/schema-types';
import { useLoggingMutation } from 'lib/apollo/apollo-hooks';
import type {
  UpdateActivitiesComponentMutation,
  UpdateActivitiesComponentMutationVariables,
} from '../../lesson-builder.graphql.generated';
import { UPDATE_ACTIVITIES_COMPONENT } from 'approot/activity-builder/lesson-builder.graphql';
import {
  modifyActivitiesPublishChanges,
  reorderComponent,
  updateExistingComponentProperties,
  updateSubActivityProperties,
} from 'approot/activity-builder/activity-builder.apollo.utils';
import { useActivityBuilderRequest } from 'approot/activity-builder/activity-builder-request.apollo';
import { removeTypename } from 'approot/shared/logic/remove-typename';
import { ActivityComponentProperties } from '../component.constants';

type ComponentInput = Omit<
  UpdateActivitiesComponentInput,
  'contentId' | 'contentType'
>;

type ComponentProperties = ActivityComponentProperties;

// `aiRationale` is a read-only output field on ActivityComponentProperties
// (set only by the Assessment Generator) and is intentionally not part of
// ComponentPropsInput. Call sites spread cached properties into the mutation
// input; this strips the field at the dispatcher boundary so GraphQL doesn't
// reject the request.
export function stripReadOnlyProperties<T extends ComponentInput>(input: T): T {
  const cleaned = removeTypename(input);
  if (cleaned.componentData?.properties) {
    delete (cleaned.componentData.properties as Record<string, unknown>)
      .aiRationale;
  }
  return cleaned;
}

type BatchComponentUpdate = {
  loading: boolean;
  updateComponentProperties: (props: {
    input: ComponentInput;
    properties?: ComponentProperties;
    typename: string;
    isSaveImmediate?: boolean;
    onCompleted?: () => void;
    onError?: () => void;
  }) => void;
};

export const BatchComponentUpdateContext = createContext<BatchComponentUpdate>({
  loading: false,
  updateComponentProperties: () => {},
});

export const BatchComponentUpdateContextProvider = ({
  children,
}: {
  children: ReactNode;
}) => {
  const { showError } = useSnackbar();

  const [loading, setLoading] = useState<boolean>(false);
  const componentSyncMapRef = useRef<Map<string, Function>>(new Map());

  const { contentId, contentType } = useGetInteractiveLessonParams();

  const { createRequest, clearRequest } = useActivityBuilderRequest();

  const [updateComponent, { client }] = useLoggingMutation<
    UpdateActivitiesComponentMutation,
    UpdateActivitiesComponentMutationVariables
  >(UPDATE_ACTIVITIES_COMPONENT);

  const syncToServer = useCallback(
    ({
      input,
      requestId,
      isSaveImmediate = false,
      onComplete,
      onError,
    }: {
      input: ComponentInput;
      requestId: string;
      isSaveImmediate?: boolean;
      onComplete: (id: string) => void;
      onError?: () => void;
    }) => {
      setLoading(isSaveImmediate);

      updateComponent({
        fetchPolicy: isSaveImmediate ? 'network-only' : 'no-cache',
        ignoreResults: !isSaveImmediate,
        variables: {
          input: {
            ...stripReadOnlyProperties(input),
            contentId,
            contentType,
          },
        },
      })
        .then(() => {
          modifyActivitiesPublishChanges({
            client,
            id: contentId,
            contentType,
          });
        })
        .catch(() => {
          if (onError) {
            onError();
          } else {
            showError(
              'There was an error saving the data. Please refresh and try again.'
            );
          }
        })
        .finally(() => {
          setLoading(false);
          onComplete(requestId);
        });
    },
    [client, contentId, contentType, updateComponent, showError]
  );

  const updateComponentProperties = useCallback(
    ({
      input,
      properties,
      typename,
      isSaveImmediate,
      onCompleted,
      onError,
    }: {
      input: ComponentInput;
      properties?: ComponentProperties;
      typename: string;
      isSaveImmediate?: boolean;
      onCompleted?: () => void;
      onError?: () => void;
    }) => {
      if (!input.componentId) return;

      if (properties) {
        updateExistingComponentProperties({
          client,
          id: input.componentId,
          typename,
          properties,
        });
      }

      if (input.componentData.sort) {
        reorderComponent({
          client,
          id: input.componentId,
          typename,
          sort: input.componentData.sort,
        });
      }

      if (input.subActivityDetails) {
        updateSubActivityProperties({
          client,
          id: input.componentId,
          typename,
          properties: input.subActivityDetails,
        });
      }

      if (isSaveImmediate) {
        const requestId = createRequest(input.componentId);
        syncToServer({
          input,
          requestId,
          isSaveImmediate,
          onComplete: () => {
            clearRequest(requestId);
            onCompleted?.();
          },
          onError,
        });
      } else {
        if (!componentSyncMapRef.current.get(input.componentId)) {
          // create a new debounce function for this componentId if it doesn't exist
          componentSyncMapRef.current.set(
            input.componentId,
            debounce((input, onComplete, isSaveImmediate, onError) => {
              const requestId = createRequest(input.componentId);
              syncToServer({
                input,
                requestId,
                isSaveImmediate,
                onComplete,
                onError,
              });
            }, 300)
          );
        }

        // call the debounced function for this componentId
        const sync = componentSyncMapRef.current.get(input.componentId);
        if (sync) {
          sync(
            input,
            (requestId: string) => {
              clearRequest(requestId);
              onCompleted?.();
            },
            isSaveImmediate,
            onError
          );
        }
      }
    },
    [client, clearRequest, createRequest, syncToServer]
  );

  const value = useMemo(
    () => ({
      loading,
      updateComponentProperties,
    }),
    [updateComponentProperties, loading]
  );

  return (
    <BatchComponentUpdateContext.Provider value={value}>
      {children}
    </BatchComponentUpdateContext.Provider>
  );
};

export const useBatchComponentUpdateContext = () => {
  const { loading, updateComponentProperties } = useContext(
    BatchComponentUpdateContext
  );

  return {
    loading,
    updateComponentProperties,
  };
};
